/**
 * Floating Bulk Action Bar Component
 */
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FolderOpen, FolderPlus, QrCode, RotateCcw, Trash2, ChevronDown, X, Calendar, CalendarClock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const BulkActionBar = ({
  selectedCount,
  selectedTags = [],
  folders,
  onMove,
  onDownloadQR,
  onReset,
  onDelete,
  onClearSelection,
  onRefresh,
  loading = false,
}) => {
  const [showMoveMenu, setShowMoveMenu] = useState(false);
  const [showDateMenu, setShowDateMenu] = useState(false);
  const [expirationDate, setExpirationDate] = useState('');
  const [goesLiveDate, setGoesLiveDate] = useState('');
  const [dateLoading, setDateLoading] = useState(false);

  if (selectedCount === 0) return null;

  const handleNewFolder = () => {
    const newName = prompt('Enter new folder name:');
    if (newName && newName.trim()) {
      onMove(newName.trim().toLowerCase().replace(/\s+/g, '-'));
    }
    setShowMoveMenu(false);
  };

  const handleBulkExpiration = async () => {
    if (!expirationDate && !goesLiveDate) {
      toast.error('Please select at least one date');
      return;
    }
    
    setDateLoading(true);
    try {
      const payload = {
        nfc_codes: selectedTags,
        expires_at: expirationDate ? new Date(expirationDate + 'T23:59:59Z').toISOString() : undefined,
        goes_live_at: goesLiveDate ? new Date(goesLiveDate).toISOString() : undefined
      };
      
      await axios.post(`${API}/admin/tags/bulk-expiration`, payload);
      toast.success(`Updated dates for ${selectedTags.length} tags`);
      setShowDateMenu(false);
      setExpirationDate('');
      setGoesLiveDate('');
      onClearSelection();
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error('Failed to update dates');
    } finally {
      setDateLoading(false);
    }
  };

  const handleClearDates = async () => {
    setDateLoading(true);
    try {
      await axios.post(`${API}/admin/tags/bulk-expiration`, {
        nfc_codes: selectedTags,
        expires_at: "",
        goes_live_at: ""
      });
      toast.success(`Cleared dates for ${selectedTags.length} tags`);
      setShowDateMenu(false);
      onClearSelection();
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error('Failed to clear dates');
    } finally {
      setDateLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40"
        data-testid="bulk-action-bar"
      >
        <div className="bg-card border border-border rounded-2xl shadow-2xl px-6 py-4 flex items-center gap-4">
          {/* Selection Count */}
          <div className="flex items-center gap-2 pr-4 border-r border-border">
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
              <span className="text-sm font-bold text-primary-foreground">{selectedCount}</span>
            </div>
            <span className="font-medium text-sm">selected</span>
          </div>
          
          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            {/* Move to Folder */}
            <div className="relative">
              <button
                onClick={() => setShowMoveMenu(!showMoveMenu)}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-secondary hover:bg-secondary/80 text-sm font-medium transition-all disabled:opacity-50"
                data-testid="bulk-move-btn"
              >
                <FolderOpen className="w-4 h-4" />
                Move
                <ChevronDown className={`w-4 h-4 transition-transform ${showMoveMenu ? 'rotate-180' : ''}`} />
              </button>
              
              {showMoveMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowMoveMenu(false)} />
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="absolute bottom-full left-0 mb-2 bg-card border border-border rounded-xl shadow-xl py-2 min-w-[180px] max-h-64 overflow-y-auto z-50"
                  >
                    <div className="px-3 py-1 text-xs text-muted-foreground font-medium">Move to folder</div>
                    {/* Unorganized option */}
                    <button
                      onClick={() => { onMove(null); setShowMoveMenu(false); }}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-secondary flex items-center gap-2"
                    >
                      <FolderOpen className="w-4 h-4 text-gray-400" />
                      Unorganized
                    </button>
                    {folders.length > 0 && <div className="border-t border-border my-1" />}
                    {folders.map(folder => (
                      <button
                        key={folder.folder_id || folder}
                        onClick={() => { onMove(folder.folder_id || folder); setShowMoveMenu(false); }}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-secondary flex items-center gap-2"
                      >
                        <FolderOpen className="w-4 h-4" style={{ color: folder.color || '#6366f1' }} />
                        {folder.name || folder}
                      </button>
                    ))}
                    <div className="border-t border-border my-1" />
                    <button
                      onClick={handleNewFolder}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-secondary flex items-center gap-2 text-primary"
                    >
                      <FolderPlus className="w-4 h-4" />
                      New Folder...
                    </button>
                  </motion.div>
                </>
              )}
            </div>
            
            {/* Download QR Codes */}
            <button
              onClick={onDownloadQR}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-secondary hover:bg-secondary/80 text-sm font-medium transition-all disabled:opacity-50"
              data-testid="bulk-qr-btn"
              title="Download QR Codes"
            >
              <QrCode className="w-4 h-4" />
              QR Codes
            </button>

            {/* Set Dates (Expiration & Schedule) */}
            <div className="relative">
              <button
                onClick={() => setShowDateMenu(!showDateMenu)}
                disabled={loading || dateLoading}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-100 text-blue-600 hover:bg-blue-200 text-sm font-medium transition-all disabled:opacity-50"
                data-testid="bulk-dates-btn"
              >
                <Calendar className="w-4 h-4" />
                Dates
                <ChevronDown className={`w-4 h-4 transition-transform ${showDateMenu ? 'rotate-180' : ''}`} />
              </button>
              
              {showDateMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowDateMenu(false)} />
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="absolute bottom-full left-0 mb-2 bg-card border border-border rounded-xl shadow-xl p-4 min-w-[280px] z-50"
                  >
                    <div className="text-sm font-medium mb-3">Set Dates for {selectedCount} tags</div>
                    
                    {/* Expiration Date */}
                    <div className="mb-3">
                      <label className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                        <Calendar className="w-3 h-3" />
                        Expiration Date
                      </label>
                      <input
                        type="date"
                        value={expirationDate}
                        onChange={(e) => setExpirationDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full bg-secondary rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                    
                    {/* Goes Live Date */}
                    <div className="mb-4">
                      <label className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                        <CalendarClock className="w-3 h-3" />
                        Goes Live Date
                      </label>
                      <input
                        type="datetime-local"
                        value={goesLiveDate}
                        onChange={(e) => setGoesLiveDate(e.target.value)}
                        min={new Date().toISOString().slice(0, 16)}
                        className="w-full bg-secondary rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                    
                    <div className="flex gap-2">
                      <button
                        onClick={handleBulkExpiration}
                        disabled={dateLoading}
                        className="flex-1 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium"
                      >
                        {dateLoading ? 'Saving...' : 'Set Dates'}
                      </button>
                      <button
                        onClick={handleClearDates}
                        disabled={dateLoading}
                        className="px-3 py-2 rounded-lg bg-secondary text-sm font-medium"
                      >
                        Clear All
                      </button>
                    </div>
                  </motion.div>
                </>
              )}
            </div>
            
            {/* Bulk Reset */}
            <button
              onClick={onReset}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-orange-100 text-orange-600 hover:bg-orange-200 text-sm font-medium transition-all disabled:opacity-50"
              data-testid="bulk-reset-btn"
            >
              <RotateCcw className="w-4 h-4" />
              Reset
            </button>
            
            {/* Bulk Delete */}
            <button
              onClick={onDelete}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-destructive/10 text-destructive hover:bg-destructive hover:text-destructive-foreground text-sm font-medium transition-all disabled:opacity-50"
              data-testid="bulk-delete-btn"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          </div>
          
          {/* Clear Selection */}
          <button
            onClick={onClearSelection}
            className="p-2 hover:bg-secondary rounded-lg transition-all ml-2"
            title="Clear selection"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default BulkActionBar;
